/**
 * AdminPlans Page
 * CRUD interface for managing subscription plans in DynamoDB.
 * Admin-only — requires Cognito 'admins' group membership.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  Edit3,
  Trash2,
  Save,
  X,
  Loader2,
  AlertTriangle,
  RefreshCw,
  Shield,
  ShieldAlert,
  ArrowLeft,
  CreditCard,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '@/contexts/AuthContext';
import { PageLayout } from '@/components';
import { Button, UnauthenticatedPage } from '@/components/ui';
import { plansApi } from '@/services/plansApi';
import { formatDate as formatDateLocale } from '@/lib/i18n-utils';
import type { Plan, PlanPricing, PlanLimits, PlanDisplay, SavePlanRequest } from '@/types/plans';

/** All known capabilities for checkbox UI */
const ALL_CAPABILITIES = [
  'record',
  'apply_template',
  'create_project',
  'insight_recommendations',
  'sync_to_cloud',
  'original_transcript_download',
];

const ICON_OPTIONS = ['user', 'rocket', 'zap', 'building2'];
const COLOR_THEME_OPTIONS = ['slate', 'blue', 'amber', 'purple'];
const BILLING_INTERVAL_OPTIONS = ['none', 'month', 'year', 'custom'];

const COLOR_THEME_CLASSES: Record<string, { bg: string; text: string }> = {
  slate: { bg: 'bg-slate-100', text: 'text-slate-700' },
  blue: { bg: 'bg-blue-100', text: 'text-blue-700' },
  amber: { bg: 'bg-amber-100', text: 'text-amber-700' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-700' },
};

interface EditorState {
  isOpen: boolean;
  mode: 'create' | 'edit';
  planId: string;
  name: string;
  displayName: string;
  sortOrder: number;
  isActive: boolean;
  pricingGbpAmount: string;
  pricingGbpFormatted: string;
  pricingGbpHeaderLine: string;
  pricingUsdAmount: string;
  pricingUsdFormatted: string;
  pricingUsdHeaderLine: string;
  currency: string;
  billingInterval: string;
  stripePriceId: string;
  isTrial: boolean;
  trialDays: number;
  description: string;
  priceDetail: string;
  pricePerMonth: string;
  features: string[];
  capabilities: string[];
  limits: PlanLimits;
  display: PlanDisplay;
  hasUnsavedChanges: boolean;
}

const initialEditorState: EditorState = {
  isOpen: false,
  mode: 'create',
  planId: '',
  name: '',
  displayName: '',
  sortOrder: 0,
  isActive: true,
  pricingGbpAmount: '0',
  pricingGbpFormatted: '',
  pricingGbpHeaderLine: '',
  pricingUsdAmount: '0',
  pricingUsdFormatted: '',
  pricingUsdHeaderLine: '',
  currency: 'GBP',
  billingInterval: 'month',
  stripePriceId: '',
  isTrial: false,
  trialDays: 0,
  description: '',
  priceDetail: '',
  pricePerMonth: '',
  features: [''],
  capabilities: ['record', 'apply_template', 'create_project'],
  limits: { recordingsPerDay: 3, maxRecordingMinutes: 30, cloudStorageGb: 0, trialDays: 0 },
  display: { icon: 'user', colorTheme: 'slate', recommended: false },
  hasUnsavedChanges: false,
};

export const AdminPlans: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading: authLoading, isAdmin } = useAuthContext();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorState>(initialEditorState);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (actionError) {
      const timer = setTimeout(() => setActionError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [actionError]);

  const fetchPlans = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    setError(null);

    try {
      const data = await plansApi.adminListPlans();
      setPlans(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : t('admin.plans.loadingPlans');
      setError(message);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [t]);

  useEffect(() => {
    if (isAuthenticated && isAdmin) {
      fetchPlans();
    }
  }, [isAuthenticated, isAdmin, fetchPlans]);

  const formatDate = (dateString: string) => {
    try {
      return formatDateLocale(dateString, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const handleCreate = () => {
    setEditor({
      ...initialEditorState,
      isOpen: true,
      mode: 'create',
    });
    setActionError(null);
  };

  const handleEdit = async (plan: Plan) => {
    setActionError(null);
    try {
      const full = await plansApi.getPlan(plan.planId);
      const gbp = full.pricing?.GBP || { amount: 0, formatted: '', headerLine: '' };
      const usd = full.pricing?.USD || { amount: 0, formatted: '', headerLine: '' };

      setEditor({
        isOpen: true,
        mode: 'edit',
        planId: full.planId,
        name: full.name || '',
        displayName: full.displayName || '',
        sortOrder: full.sortOrder || 0,
        isActive: full.isActive ?? true,
        pricingGbpAmount: String(gbp.amount || 0),
        pricingGbpFormatted: gbp.formatted || '',
        pricingGbpHeaderLine: gbp.headerLine || '',
        pricingUsdAmount: String(usd.amount || 0),
        pricingUsdFormatted: usd.formatted || '',
        pricingUsdHeaderLine: usd.headerLine || '',
        currency: full.currency || 'GBP',
        billingInterval: full.billingInterval || 'month',
        stripePriceId: full.stripePriceId || '',
        isTrial: full.isTrial ?? false,
        trialDays: full.trialDays || 0,
        description: full.description || '',
        priceDetail: full.priceDetail || '',
        pricePerMonth: full.pricePerMonth || '',
        features: full.features?.length ? full.features : [''],
        capabilities: full.capabilities || [],
        limits: full.limits || { recordingsPerDay: 3, maxRecordingMinutes: 30, cloudStorageGb: 0, trialDays: 0 },
        display: full.display || { icon: 'user', colorTheme: 'slate', recommended: false },
        hasUnsavedChanges: false,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : t('admin.plans.loadingPlans');
      setActionError(message);
    }
  };

  const handleCloseEditor = useCallback(() => {
    if (editor.hasUnsavedChanges) {
      if (!window.confirm(t('admin.plans.unsavedConfirm'))) {
        return;
      }
    }
    setEditor(initialEditorState);
  }, [editor.hasUnsavedChanges, t]);

  const handleSave = async () => {
    if (!editor.name.trim()) {
      setActionError(t('admin.plans.validationPlanName'));
      return;
    }
    if (!editor.displayName.trim()) {
      setActionError(t('admin.plans.validationDisplayName'));
      return;
    }
    if (editor.mode === 'create' && !editor.planId.trim()) {
      setActionError(t('admin.plans.validationPlanId'));
      return;
    }

    setIsSaving(true);
    setActionError(null);

    try {
      const pricing: Record<string, PlanPricing> = {
        GBP: {
          amount: parseFloat(editor.pricingGbpAmount) || 0,
          formatted: editor.pricingGbpFormatted.trim(),
          headerLine: editor.pricingGbpHeaderLine.trim(),
        },
        USD: {
          amount: parseFloat(editor.pricingUsdAmount) || 0,
          formatted: editor.pricingUsdFormatted.trim(),
          headerLine: editor.pricingUsdHeaderLine.trim(),
        },
      };

      const cleanFeatures = editor.features.filter(f => f.trim() !== '');

      const request: SavePlanRequest = {
        name: editor.name.trim(),
        displayName: editor.displayName.trim(),
        sortOrder: editor.sortOrder,
        isActive: editor.isActive,
        pricing,
        currency: editor.currency,
        billingInterval: editor.billingInterval,
        stripePriceId: editor.stripePriceId.trim(),
        isTrial: editor.isTrial,
        trialDays: editor.trialDays,
        description: editor.description.trim(),
        priceDetail: editor.priceDetail.trim(),
        pricePerMonth: editor.pricePerMonth.trim(),
        features: cleanFeatures,
        capabilities: editor.capabilities,
        limits: editor.limits,
        display: editor.display,
      };

      if (editor.mode === 'create') {
        request.planId = editor.planId.trim();
        await plansApi.createPlan(request);
      } else {
        await plansApi.updatePlan(editor.planId, request);
      }

      setEditor(initialEditorState);
      await fetchPlans();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('admin.plans.saving');
      setActionError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (planId: string) => {
    setActionError(null);
    try {
      await plansApi.deletePlan(planId);
      setDeleteConfirm(null);
      await fetchPlans();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('admin.plans.delete');
      setActionError(message);
    }
  };

  // Feature list management
  const addFeature = () => {
    setEditor(prev => ({
      ...prev,
      features: [...prev.features, ''],
      hasUnsavedChanges: true,
    }));
  };

  const updateFeature = (index: number, value: string) => {
    setEditor(prev => ({
      ...prev,
      features: prev.features.map((f, i) => i === index ? value : f),
      hasUnsavedChanges: true,
    }));
  };

  const removeFeature = (index: number) => {
    setEditor(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
      hasUnsavedChanges: true,
    }));
  };

  // Capability toggle
  const toggleCapability = (cap: string) => {
    setEditor(prev => ({
      ...prev,
      capabilities: prev.capabilities.includes(cap)
        ? prev.capabilities.filter(c => c !== cap)
        : [...prev.capabilities, cap],
      hasUnsavedChanges: true,
    }));
  };

  if (authLoading) {
    return (
      <PageLayout>
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-12 sm:pt-24">
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!isAuthenticated) {
    return (
      <UnauthenticatedPage
        icon={<Shield className="w-8 h-8 text-slate-400" />}
        title={t('admin.plans.unauthTitle')}
        description={t('admin.plans.unauthDescription')}
      />
    );
  }

  if (!isAdmin) {
    return (
      <PageLayout>
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-12 sm:pt-24">
          <div className="text-center py-16">
            <ShieldAlert className="w-16 h-16 mx-auto mb-4 text-red-400" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('admin.plans.accessDenied')}</h1>
            <p className="text-slate-600 mb-6">{t('admin.plans.noPermission')}</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-slate-600 text-white rounded-lg font-medium hover:bg-slate-700 transition-colors"
            >
              {t('admin.plans.goHome')}
            </Link>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (isLoading && plans.length === 0) {
    return (
      <PageLayout>
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-12 sm:pt-24">
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mr-3" />
            <span className="text-slate-600">{t('admin.plans.loadingPlans')}</span>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto px-6 pt-20 pb-12 sm:pt-24">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Link
                to="/admin"
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                title={t('admin.plans.backToAdmin')}
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="p-2 bg-emerald-100 rounded-lg">
                <CreditCard className="w-6 h-6 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900">{t('admin.plans.title')}</h1>
            </div>
            <p className="text-slate-600 ml-[88px]">
              {t('admin.plans.planCount', { count: plans.length })}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => fetchPlans(true)}
              disabled={isRefreshing}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              {t('admin.plans.refresh')}
            </button>
            <button
              onClick={handleCreate}
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              {t('admin.plans.newPlan')}
            </button>
          </div>
        </div>

        {/* Error banner */}
        {(error || actionError) && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
            <p className="text-red-700">{actionError || error}</p>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && plans.length === 0 && (
          <div className="text-center py-16 bg-slate-50 rounded-2xl border border-slate-200">
            <CreditCard className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2">{t('admin.plans.noPlansTitle')}</h2>
            <p className="text-slate-500 mb-6">
              {t('admin.plans.noPlansDescription')}
            </p>
            <button
              onClick={handleCreate}
              className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              {t('admin.plans.createPlan')}
            </button>
          </div>
        )}

        {/* Plans list */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map(plan => {
              const colors = COLOR_THEME_CLASSES[plan.display?.colorTheme] || COLOR_THEME_CLASSES.slate;
              return (
                <div
                  key={plan.planId}
                  className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ${colors.bg} ${colors.text}`}>
                        {plan.display?.colorTheme || 'slate'}
                      </span>
                      {plan.display?.recommended && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                          {t('admin.plans.recommended')}
                        </span>
                      )}
                    </div>
                    {plan.isActive ? (
                      <ToggleRight className="w-5 h-5 text-emerald-500 shrink-0" />
                    ) : (
                      <ToggleLeft className="w-5 h-5 text-slate-300 shrink-0" />
                    )}
                  </div>

                  <h3 className="font-semibold text-slate-900 text-lg">{plan.displayName}</h3>
                  <p className="text-xs text-slate-400 font-mono mb-1">{plan.planId}</p>

                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="text-2xl font-bold text-slate-900">
                      {plan.pricing?.GBP?.formatted || 'N/A'}
                    </span>
                    <span className="text-sm text-slate-500">{plan.priceDetail}</span>
                  </div>

                  <p className="text-sm text-slate-600 mb-3 line-clamp-2">{plan.description}</p>

                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded">
                      {t('admin.plans.orderLabel', { order: plan.sortOrder })}
                    </span>
                    <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded">
                      {t('admin.plans.featuresCount', { count: plan.features?.length || 0 })}
                    </span>
                    <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded">
                      {t('admin.plans.capsCount', { count: plan.capabilities?.length || 0 })}
                    </span>
                  </div>

                  <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                    <span className="text-xs text-slate-400">
                      {plan.updatedAt ? t('admin.plans.updatedDate', { date: formatDate(plan.updatedAt) }) : t('admin.plans.notSaved')}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleEdit(plan)}
                        className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                        title={t('admin.plans.editButton')}
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(plan.planId)}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title={t('admin.plans.deleteButton')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>

        {/* Delete confirmation modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-xl">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">{t('admin.plans.deleteModalTitle')}</h3>
              <p className="text-slate-600 mb-6">
                {t('admin.plans.deleteModalDescription')}
              </p>
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  {t('admin.plans.cancel')}
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  {t('admin.plans.delete')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Editor modal */}
        {editor.isOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-4">
            <div
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
              onClick={handleCloseEditor}
            />
            <div className="relative bg-white w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-4xl sm:rounded-2xl shadow-2xl flex flex-col">
              {/* Modal header */}
              <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <CreditCard className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-base sm:text-xl font-bold text-slate-900">
                        {editor.mode === 'create' ? t('admin.plans.editorCreateTitle') : editor.displayName || t('admin.plans.editorEditTitle')}
                      </h3>
                      {editor.hasUnsavedChanges && (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                          {t('admin.plans.unsaved')}
                        </span>
                      )}
                    </div>
                    {editor.mode === 'edit' && (
                      <p className="text-xs sm:text-sm text-slate-500 mt-0.5 font-mono">{editor.planId}</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={handleCloseEditor}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal body */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5">
                {/* Action error inside modal */}
                {actionError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{actionError}</p>
                  </div>
                )}

                {/* Section: Basic Info */}
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wider">{t('admin.plans.sectionBasicInfo')}</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.plans.labelPlanId')}</label>
                      <input
                        type="text"
                        value={editor.planId}
                        onChange={e => setEditor(prev => ({ ...prev, planId: e.target.value, hasUnsavedChanges: true }))}
                        placeholder={t('admin.plans.placeholderPlanId')}
                        disabled={editor.mode === 'edit'}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-mono text-sm disabled:bg-slate-50 disabled:text-slate-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.plans.labelName')}</label>
                      <input
                        type="text"
                        value={editor.name}
                        onChange={e => setEditor(prev => ({ ...prev, name: e.target.value, hasUnsavedChanges: true }))}
                        placeholder={t('admin.plans.placeholderName')}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.plans.labelDisplayName')}</label>
                      <input
                        type="text"
                        value={editor.displayName}
                        onChange={e => setEditor(prev => ({ ...prev, displayName: e.target.value, hasUnsavedChanges: true }))}
                        placeholder={t('admin.plans.placeholderDisplayName')}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.plans.labelSortOrder')}</label>
                      <input
                        type="number"
                        value={editor.sortOrder}
                        onChange={e => setEditor(prev => ({ ...prev, sortOrder: parseInt(e.target.value) || 0, hasUnsavedChanges: true }))}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.plans.labelBillingInterval')}</label>
                      <select
                        value={editor.billingInterval}
                        onChange={e => setEditor(prev => ({ ...prev, billingInterval: e.target.value, hasUnsavedChanges: true }))}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      >
                        {BILLING_INTERVAL_OPTIONS.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-end pb-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editor.isActive}
                          onChange={e => setEditor(prev => ({ ...prev, isActive: e.target.checked, hasUnsavedChanges: true }))}
                          className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                        />
                        <span className="text-sm font-medium text-slate-700">{t('admin.plans.labelActive')}</span>
                      </label>
                    </div>
                    <div className="flex items-end pb-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editor.isTrial}
                          onChange={e => setEditor(prev => ({ ...prev, isTrial: e.target.checked, hasUnsavedChanges: true }))}
                          className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                        />
                        <span className="text-sm font-medium text-slate-700">{t('admin.plans.labelTrialPlan')}</span>
                      </label>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.plans.labelDescription')}</label>
                      <input
                        type="text"
                        value={editor.description}
                        onChange={e => setEditor(prev => ({ ...prev, description: e.target.value, hasUnsavedChanges: true }))}
                        placeholder={t('admin.plans.placeholderDescription')}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.plans.labelPriceDetail')}</label>
                      <input
                        type="text"
                        value={editor.priceDetail}
                        onChange={e => setEditor(prev => ({ ...prev, priceDetail: e.target.value, hasUnsavedChanges: true }))}
                        placeholder={t('admin.plans.placeholderPriceDetail')}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.plans.labelPricePerMonth')}</label>
                      <input
                        type="text"
                        value={editor.pricePerMonth}
                        onChange={e => setEditor(prev => ({ ...prev, pricePerMonth: e.target.value, hasUnsavedChanges: true }))}
                        placeholder={t('admin.plans.placeholderPricePerMonth')}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Section: Pricing */}
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wider">{t('admin.plans.sectionPricing')}</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* GBP */}
                    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <p className="text-sm font-medium text-slate-700 mb-3">{t('admin.plans.gbpLabel')}</p>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">{t('admin.plans.labelAmount')}</label>
                          <input
                            type="number"
                            step="0.01"
                            value={editor.pricingGbpAmount}
                            onChange={e => setEditor(prev => ({ ...prev, pricingGbpAmount: e.target.value, hasUnsavedChanges: true }))}
                            className="w-full px-3 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">{t('admin.plans.labelFormattedGbp')}</label>
                          <input
                            type="text"
                            value={editor.pricingGbpFormatted}
                            onChange={e => setEditor(prev => ({ ...prev, pricingGbpFormatted: e.target.value, hasUnsavedChanges: true }))}
                            className="w-full px-3 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">{t('admin.plans.labelHeaderLine')}</label>
                          <input
                            type="text"
                            value={editor.pricingGbpHeaderLine}
                            onChange={e => setEditor(prev => ({ ...prev, pricingGbpHeaderLine: e.target.value, hasUnsavedChanges: true }))}
                            placeholder={t('admin.plans.placeholderHeaderLineGbp')}
                            className="w-full px-3 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                          />
                        </div>
                      </div>
                    </div>

                    {/* USD */}
                    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <p className="text-sm font-medium text-slate-700 mb-3">{t('admin.plans.usdLabel')}</p>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">{t('admin.plans.labelAmount')}</label>
                          <input
                            type="number"
                            step="0.01"
                            value={editor.pricingUsdAmount}
                            onChange={e => setEditor(prev => ({ ...prev, pricingUsdAmount: e.target.value, hasUnsavedChanges: true }))}
                            className="w-full px-3 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">{t('admin.plans.labelFormattedUsd')}</label>
                          <input
                            type="text"
                            value={editor.pricingUsdFormatted}
                            onChange={e => setEditor(prev => ({ ...prev, pricingUsdFormatted: e.target.value, hasUnsavedChanges: true }))}
                            className="w-full px-3 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">{t('admin.plans.labelHeaderLine')}</label>
                          <input
                            type="text"
                            value={editor.pricingUsdHeaderLine}
                            onChange={e => setEditor(prev => ({ ...prev, pricingUsdHeaderLine: e.target.value, hasUnsavedChanges: true }))}
                            placeholder={t('admin.plans.placeholderHeaderLineUsd')}
                            className="w-full px-3 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.plans.labelStripePriceId')}</label>
                      <input
                        type="text"
                        value={editor.stripePriceId}
                        onChange={e => setEditor(prev => ({ ...prev, stripePriceId: e.target.value, hasUnsavedChanges: true }))}
                        placeholder={t('admin.plans.placeholderStripePriceId')}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-mono text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.plans.labelTrialDays')}</label>
                      <input
                        type="number"
                        value={editor.trialDays}
                        onChange={e => setEditor(prev => ({ ...prev, trialDays: parseInt(e.target.value) || 0, hasUnsavedChanges: true }))}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Section: Features */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wider">{t('admin.plans.sectionFeatures')}</h4>
                    <button
                      type="button"
                      onClick={addFeature}
                      className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                    >
                      {t('admin.plans.addFeature')}
                    </button>
                  </div>
                  <div className="space-y-2">
                    {editor.features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={feature}
                          onChange={e => updateFeature(index, e.target.value)}
                          placeholder={t('admin.plans.placeholderFeature')}
                          className="flex-1 px-3 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => removeFeature(index)}
                          className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                          title={t('admin.plans.removeButton')}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Section: Capabilities */}
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wider">{t('admin.plans.sectionCapabilities')}</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {ALL_CAPABILITIES.map(cap => (
                      <label key={cap} className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-slate-50">
                        <input
                          type="checkbox"
                          checked={editor.capabilities.includes(cap)}
                          onChange={() => toggleCapability(cap)}
                          className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                        />
                        <span className="text-sm text-slate-700">{cap.replace(/_/g, ' ')}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Section: Limits */}
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wider">{t('admin.plans.sectionLimits')}</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">{t('admin.plans.labelRecordingsPerDay')}</label>
                      <input
                        type="number"
                        value={editor.limits.recordingsPerDay}
                        onChange={e => setEditor(prev => ({
                          ...prev,
                          limits: { ...prev.limits, recordingsPerDay: parseInt(e.target.value) || 0 },
                          hasUnsavedChanges: true,
                        }))}
                        className="w-full px-3 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">{t('admin.plans.labelMaxRecordingMins')}</label>
                      <input
                        type="number"
                        value={editor.limits.maxRecordingMinutes}
                        onChange={e => setEditor(prev => ({
                          ...prev,
                          limits: { ...prev.limits, maxRecordingMinutes: parseInt(e.target.value) || 0 },
                          hasUnsavedChanges: true,
                        }))}
                        className="w-full px-3 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">{t('admin.plans.labelCloudStorageGb')}</label>
                      <input
                        type="number"
                        value={editor.limits.cloudStorageGb}
                        onChange={e => setEditor(prev => ({
                          ...prev,
                          limits: { ...prev.limits, cloudStorageGb: parseInt(e.target.value) || 0 },
                          hasUnsavedChanges: true,
                        }))}
                        className="w-full px-3 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">{t('admin.plans.labelLimitTrialDays')}</label>
                      <input
                        type="number"
                        value={editor.limits.trialDays}
                        onChange={e => setEditor(prev => ({
                          ...prev,
                          limits: { ...prev.limits, trialDays: parseInt(e.target.value) || 0 },
                          hasUnsavedChanges: true,
                        }))}
                        className="w-full px-3 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Section: Display */}
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wider">{t('admin.plans.sectionDisplaySettings')}</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.plans.labelIcon')}</label>
                      <select
                        value={editor.display.icon}
                        onChange={e => setEditor(prev => ({
                          ...prev,
                          display: { ...prev.display, icon: e.target.value },
                          hasUnsavedChanges: true,
                        }))}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      >
                        {ICON_OPTIONS.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">{t('admin.plans.labelColorTheme')}</label>
                      <select
                        value={editor.display.colorTheme}
                        onChange={e => setEditor(prev => ({
                          ...prev,
                          display: { ...prev.display, colorTheme: e.target.value },
                          hasUnsavedChanges: true,
                        }))}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      >
                        {COLOR_THEME_OPTIONS.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-end pb-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editor.display.recommended}
                          onChange={e => setEditor(prev => ({
                            ...prev,
                            display: { ...prev.display, recommended: e.target.checked },
                            hasUnsavedChanges: true,
                          }))}
                          className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                        />
                        <span className="text-sm font-medium text-slate-700">{t('admin.plans.labelRecommended')}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal footer */}
              <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-t border-slate-200 bg-slate-50 sm:rounded-b-2xl">
                <button
                  onClick={handleCloseEditor}
                  disabled={isSaving}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  {editor.hasUnsavedChanges ? t('admin.plans.discard') : t('admin.plans.cancel')}
                </button>
                <Button
                  onClick={handleSave}
                  disabled={isSaving || !editor.name.trim() || !editor.displayName.trim()}
                  variant="gradient-emerald"
                  size="md"
                  shape="default"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('admin.plans.saving')}
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      {editor.mode === 'create' ? t('admin.plans.createPlan') : t('admin.plans.saveChanges')}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default AdminPlans;
